// Smart Title Case for person + dog names. Applied at the write boundary so a
// stored name is cased consistently however it was typed (ALL CAPS, lower,
// mixed). Preserves the name shapes a Wicklow kennel actually sees: Mc prefixes
// (McDonald), O' apostrophes (O'Brien), and hyphenated double-barrels
// (Anne-Marie). Unicode-aware so accented names (Aoife, Séan) survive.
//
// Deliberately NOT clever about Mac — "MacLeod" is real but so is "Mackenzie"
// and "Mack", and there's no safe rule, so Mac is left as plain Title Case.
export function titleCaseName(input: string): string {
  const trimmed = input.trim().replace(/\s+/g, ' ');
  if (!trimmed) return '';
  // Lowercase everything, then capitalise the first letter and any letter that
  // follows a separator — space, hyphen, or a straight/curly apostrophe.
  let out = trimmed
    .toLowerCase()
    .replace(/(^|[\s'’-])(\p{L})/gu, (_, sep: string, ch: string) => sep + ch.toUpperCase());
  // "Mcdonald" → "McDonald": capitalise the letter after a leading "Mc".
  out = out.replace(/\bMc(\p{L})/gu, (_, ch: string) => 'Mc' + ch.toUpperCase());
  return out;
}

// --- the contact name split (portal-foundations AC5) --------------------
//
// `owner_contacts.name` gains `first_name` and `last_name` BESIDE it in
// migration 0024; the column itself stays, which is what keeps the split
// re-runnable and the five readers of `oc.name` untouched (AC5a).
//
// Two callers, one rule: 0024 backfills existing rows in SQL, and every
// contact written after it splits here in TypeScript. They are emitted from
// one place for the reason notYetEndedSql is — an inlined predicate drifted
// twice before it was centralised.
//
// The rule is the FIRST space and nothing cleverer. "Mary Jane Cassidy" is
// Mary + "Jane Cassidy"; there is no way to know whether Jane is a middle name
// or half a double-barrel, and guessing would be wrong silently. A name with
// no space at all is a first name with no surname — Wicklow has plenty.

export interface ContactNameParts {
  firstName: string | null;
  lastName: string | null;
}

export function splitContactName(input: string): ContactNameParts {
  // Same normalisation as titleCaseName so the two agree on what a gap is:
  // trim the outside, collapse runs of whitespace to one space.
  const trimmed = (input ?? "").trim().replace(/\s+/g, " ");
  if (!trimmed) return { firstName: null, lastName: null };

  const gap = trimmed.indexOf(" ");
  if (gap === -1) return { firstName: trimmed, lastName: null };

  return {
    firstName: trimmed.slice(0, gap),
    lastName: trimmed.slice(gap + 1),
  };
}

// The same rule as SQL, for the 0024 backfill. `column` is interpolated, so it
// takes a column reference this module controls ("name", "oc.name") and never
// user input — the values are never interpolated, only the identifier.
export function splitContactNameSql(column: string): {
  firstName: string;
  lastName: string;
} {
  // btrim + regexp_replace mirror the TS trim-and-collapse exactly; without
  // the collapse, "Mary  Jane" splits into "Mary" + " Jane" in SQL and "Mary"
  // + "Jane" in TypeScript, and the backfill disagrees with every row written
  // after it.
  const norm = `btrim(regexp_replace(${column}, '\\s+', ' ', 'g'))`;

  return {
    firstName: `NULLIF(split_part(${norm}, ' ', 1), '')`,
    // The guard is the subtlest line in the migration. strpos returns 0 when
    // there is no space, and substr(x, 0 + 1) is the whole string — so without
    // the CASE, "Maeve" would land in BOTH columns.
    lastName:
      `CASE WHEN strpos(${norm}, ' ') = 0 THEN NULL ` +
      `ELSE NULLIF(substr(${norm}, strpos(${norm}, ' ') + 1), '') END`,
  };
}
